import { FC, MouseEvent, ReactNode, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Box, CircularProgress, Stack } from "@mui/material";

import { INestedTrain, useAsu } from "@framework/provider-asu";
import { useApiCall } from "@framework/react-hooks";
import { useUser } from "@framework/provider-user";
import {
  IUser,
  pickStationTileAnalyticsRowIds,
  DEFAULT_STATION_TILE_ANALYTICS_ROWS_VIEW,
  normalizeStationTileAnalyticsRowsVisibility,
} from "@framework/types";
import { SortDirection } from "@framework/types-collection";

import { getModeColor } from "../../../../../../../../components/common/main-menu/equipment/icon-color";
import { useAlarmPaletteResolver } from "../../../../../../../../hooks/use-alarm-color";
import { getMaxAlarmsStatusPointsOrVisualControl } from "../../../../../../../../utils/get-max-alarm-status";
import { mapApiTrainTiles, IStationTrainTileApiDto } from "../station-train/map-api-train-tiles";
import { StationTrain } from "../station-train";
import { IStationTrainSortItem, sortStationTrainIds, StationTrainSortField } from "./station-train-sort";
import { StationTrainSortToolbar } from "./station-train-sort-toolbar";
import { openPopupWithParams } from "../image-view/components/SchemeArea/utils";

interface IEquipmentGroupOption {
  id: number;
  title: string;
}

interface ITrainTypeOption {
  id: number;
  title: string;
}

interface ITrainForecastStatus {
  idTrain: number;
  status: number | null;
}

interface ITrainTciValue {
  idTrain: number;
  tciValue: number | null;
}

const toIsoTimestamp = (value: string | Date): string => (typeof value === "string" ? value : value.toISOString());

const parseNullableNumber = (value: unknown): number | null => {
  if (value === null || value === undefined || value === "") {
    return null;
  }

  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : null;
};

const STATION_TRAIN_TILE_OUTER_WIDTH = 336;
const STATION_TRAIN_TILE_OUTER_HEIGHT = 272;

const calcVisibleStationTrainTileCount = (containerWidth: number, containerHeight: number): number => {
  const width = containerWidth > 0 ? containerWidth : STATION_TRAIN_TILE_OUTER_WIDTH;
  const height = containerHeight > 0 ? containerHeight : STATION_TRAIN_TILE_OUTER_HEIGHT;
  const columns = Math.max(1, Math.floor(width / STATION_TRAIN_TILE_OUTER_WIDTH));
  const rows = Math.max(1, Math.floor(height / STATION_TRAIN_TILE_OUTER_HEIGHT));

  return columns * rows;
};

const DEFAULT_TILE_BATCH_SIZE =
  calcVisibleStationTrainTileCount(STATION_TRAIN_TILE_OUTER_WIDTH, STATION_TRAIN_TILE_OUTER_HEIGHT) * 2;

const calcStationTrainTileBatchSize = (containerWidth: number, containerHeight: number): number =>
  calcVisibleStationTrainTileCount(containerWidth, containerHeight) * 2;

export interface IStationSchemeDefaultViewProps {
  trains: Record<number, INestedTrain>;
  headerActions?: ReactNode;
}

export const StationSchemeDefaultView: FC<IStationSchemeDefaultViewProps> = props => {
  const { trains, headerActions } = props;
  const { getTrain, isVisualControlOpen, selectedItem, selectTrain, startTimestamp, endTimestamp } = useAsu();
  const user = useUser<IUser>();
  const resolveAlarmColor = useAlarmPaletteResolver();
  const schemeInNewWindow = user.profile.interfaceSettings.openTrainSchemeInNewWindow;

  const [displayCount, setDisplayCount] = useState(DEFAULT_TILE_BATCH_SIZE);
  const [isForecastSortLoading, setIsForecastSortLoading] = useState(false);
  const [isTciSortLoading, setIsTciSortLoading] = useState(false);
  const [isLoadingMoreTiles, setIsLoadingMoreTiles] = useState(false);
  const [sortField, setSortField] = useState<StationTrainSortField>(StationTrainSortField.custom);
  const [sortDirection, setSortDirection] = useState<SortDirection>(SortDirection.asc);
  const [equipmentGroups, setEquipmentGroups] = useState<IEquipmentGroupOption[]>([]);
  const [trainTypes, setTrainTypes] = useState<ITrainTypeOption[]>([]);
  const [forecastStatusByTrainId, setForecastStatusByTrainId] = useState<Record<number, number | null>>({});
  const [tciValueByTrainId, setTciValueByTrainId] = useState<Record<number, number | null>>({});
  const [tileDataByTrainId, setTileDataByTrainId] = useState<
    Record<number, ReturnType<typeof mapApiTrainTiles>[number]>
  >({});

  const scrollRef = useRef<HTMLDivElement>(null);
  const sentinelRef = useRef<HTMLDivElement>(null);
  const batchSizeRef = useRef(DEFAULT_TILE_BATCH_SIZE);
  const loadedTrainIdsRef = useRef<Set<number>>(new Set());
  const inFlightTrainIdsRef = useRef<Set<number>>(new Set());
  const tilesRequestGenerationRef = useRef(0);
  const activeTilesRequestKeyRef = useRef<string | null>(null);
  const dictionariesLoadedRef = useRef(false);

  const resolveInitialDisplayCount = () => {
    const scrollElement = scrollRef.current;
    if (scrollElement && scrollElement.clientWidth > 0 && scrollElement.clientHeight > 0) {
      return calcStationTrainTileBatchSize(scrollElement.clientWidth, scrollElement.clientHeight);
    }
    return batchSizeRef.current;
  };

  const analyticsRowsView =
    user.profile.interfaceSettings.ts_scheme?.stationTileAnalyticsRowsView ?? DEFAULT_STATION_TILE_ANALYTICS_ROWS_VIEW;
  const analyticsRowsVisibility = useMemo(
    () =>
      normalizeStationTileAnalyticsRowsVisibility(
        user.profile.interfaceSettings.ts_scheme?.stationTileAnalyticsRowsVisibility,
      ),
    [user.profile.interfaceSettings.ts_scheme?.stationTileAnalyticsRowsVisibility],
  );
  const selectedAnalyticsRowIds = useMemo(
    () => pickStationTileAnalyticsRowIds(analyticsRowsView, analyticsRowsVisibility),
    [analyticsRowsView, analyticsRowsVisibility],
  );
  const selectedAnalyticsRows = useMemo(() => selectedAnalyticsRowIds.join(","), [selectedAnalyticsRowIds]);

  const stationId = selectedItem && "Id" in selectedItem ? selectedItem.Id : null;
  const isSortDataLoading = isForecastSortLoading || isTciSortLoading;

  const tilesListContextKey = useMemo(() => {
    if (!stationId || !startTimestamp || !endTimestamp) {
      return null;
    }

    return [
      stationId,
      toIsoTimestamp(startTimestamp),
      toIsoTimestamp(endTimestamp),
      selectedAnalyticsRows,
      sortField,
      sortDirection,
    ].join("|");
  }, [stationId, startTimestamp, endTimestamp, selectedAnalyticsRows, sortField, sortDirection]);

  const equipmentGroupNames = useMemo(() => {
    return equipmentGroups.reduce<Record<number, string>>((acc, group) => {
      acc[group.id] = group.title;
      return acc;
    }, {});
  }, [equipmentGroups]);

  const customTrainTypeNames = useMemo(() => {
    return trainTypes.reduce<Record<number, string>>((acc, type) => {
      acc[type.id] = type.title;
      return acc;
    }, {});
  }, [trainTypes]);

  const { fn: loadEquipmentGroups } = useApiCall(
    async (api): Promise<IEquipmentGroupOption[]> => {
      const response: unknown = await api.fetchJson({
        url: "/equipment-group/autocomplete",
        method: "GET",
      });
      return Array.isArray(response) ? (response as IEquipmentGroupOption[]) : [];
    },
    { success: false },
  );

  const { fn: loadTrainTypes } = useApiCall(
    async (api): Promise<ITrainTypeOption[]> => {
      const response: unknown = await api.fetchJson({
        url: "/train-type/autocomplete",
        method: "GET",
      });
      return Array.isArray(response) ? (response as ITrainTypeOption[]) : [];
    },
    { success: false },
  );

  const { fn: loadTrainForecastStatuses } = useApiCall(
    async (
      api,
      {
        idTopLevel,
        startTimestamp: start,
        endTimestamp: end,
      }: {
        idTopLevel: number;
        startTimestamp: string | Date;
        endTimestamp: string | Date;
      },
    ): Promise<ITrainForecastStatus[]> => {
      const response: unknown = await api.fetchJson({
        url: `/stations/${idTopLevel}/train-forecast-statuses`,
        method: "GET",
        data: {
          startTimestamp: toIsoTimestamp(start),
          endTimestamp: toIsoTimestamp(end),
        },
      });
      return Array.isArray(response) ? (response as ITrainForecastStatus[]) : [];
    },
    { success: false, error: false },
  );

  const { fn: loadTrainTciValues } = useApiCall(
    async (
      api,
      {
        idTopLevel,
        startTimestamp: start,
        endTimestamp: end,
      }: {
        idTopLevel: number;
        startTimestamp: string | Date;
        endTimestamp: string | Date;
      },
    ): Promise<ITrainTciValue[]> => {
      const response: unknown = await api.fetchJson({
        url: `/stations/${idTopLevel}/train-tci-values`,
        method: "GET",
        data: {
          startTimestamp: toIsoTimestamp(start),
          endTimestamp: toIsoTimestamp(end),
        },
      });
      return Array.isArray(response) ? (response as ITrainTciValue[]) : [];
    },
    { success: false, error: false },
  );

  const { fn: loadTrainTiles } = useApiCall(
    async (
      api,
      {
        idTopLevel,
        startTimestamp: start,
        endTimestamp: end,
        trainIds,
      }: {
        idTopLevel: number;
        startTimestamp: string | Date;
        endTimestamp: string | Date;
        trainIds: string;
      },
    ): Promise<IStationTrainTileApiDto[]> => {
      const response: unknown = await api.fetchJson({
        url: `/stations/${idTopLevel}/train-tiles`,
        method: "GET",
        data: {
          startTimestamp: toIsoTimestamp(start),
          endTimestamp: toIsoTimestamp(end),
          trainIds,
        },
      });
      return Array.isArray(response) ? (response as IStationTrainTileApiDto[]) : [];
    },
    { success: false, error: false },
  );

  useEffect(() => {
    if (dictionariesLoadedRef.current) {
      return;
    }
    dictionariesLoadedRef.current = true;

    void loadEquipmentGroups(undefined)
      .then(response => {
        if (Array.isArray(response)) {
          setEquipmentGroups(response);
        }
      })
      .catch(() => undefined);

    void loadTrainTypes(undefined)
      .then(response => {
        if (Array.isArray(response)) {
          setTrainTypes(response);
        }
      })
      .catch(() => undefined);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const scrollElement = scrollRef.current;
    if (!scrollElement) {
      return;
    }

    const updateBatchSize = () => {
      const nextBatchSize = calcStationTrainTileBatchSize(scrollElement.clientWidth, scrollElement.clientHeight);
      batchSizeRef.current = nextBatchSize;
    };

    updateBatchSize();

    const resizeObserver = new ResizeObserver(updateBatchSize);
    resizeObserver.observe(scrollElement);

    return () => resizeObserver.disconnect();
  }, []);

  useEffect(() => {
    if (!tilesListContextKey) {
      loadedTrainIdsRef.current = new Set();
      inFlightTrainIdsRef.current = new Set();
      activeTilesRequestKeyRef.current = null;
      setTileDataByTrainId({});
      setDisplayCount(resolveInitialDisplayCount());
      return;
    }

    tilesRequestGenerationRef.current += 1;
    loadedTrainIdsRef.current = new Set();
    inFlightTrainIdsRef.current = new Set();
    activeTilesRequestKeyRef.current = null;
    setTileDataByTrainId({});
    setDisplayCount(resolveInitialDisplayCount());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tilesListContextKey]);

  useEffect(() => {
    if (!stationId || !startTimestamp || !endTimestamp || sortField !== StationTrainSortField.forecast) {
      setForecastStatusByTrainId({});
      setIsForecastSortLoading(false);
      return;
    }

    let isCurrentRequest = true;
    setForecastStatusByTrainId({});
    setIsForecastSortLoading(true);

    void loadTrainForecastStatuses(undefined, {
      idTopLevel: stationId,
      startTimestamp,
      endTimestamp,
    })
      .then(response => {
        if (!isCurrentRequest) {
          return;
        }

        const items = Array.isArray(response) ? response : [];
        setForecastStatusByTrainId(
          items.reduce<Record<number, number | null>>((statuses, item) => {
            const idTrain = Number(item.idTrain);
            if (Number.isFinite(idTrain)) {
              statuses[idTrain] = parseNullableNumber(item.status);
            }
            return statuses;
          }, {}),
        );
      })
      .catch(() => {
        if (isCurrentRequest) {
          setForecastStatusByTrainId({});
        }
      })
      .finally(() => {
        if (isCurrentRequest) {
          setIsForecastSortLoading(false);
        }
      });

    return () => {
      isCurrentRequest = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [stationId, startTimestamp, endTimestamp, sortField]);

  useEffect(() => {
    if (!stationId || !startTimestamp || !endTimestamp || sortField !== StationTrainSortField.tci) {
      setTciValueByTrainId({});
      setIsTciSortLoading(false);
      return;
    }

    let isCurrentRequest = true;
    setTciValueByTrainId({});
    setIsTciSortLoading(true);

    void loadTrainTciValues(undefined, {
      idTopLevel: stationId,
      startTimestamp,
      endTimestamp,
    })
      .then(response => {
        if (!isCurrentRequest) {
          return;
        }

        const items = Array.isArray(response) ? response : [];
        setTciValueByTrainId(
          items.reduce<Record<number, number | null>>((values, item) => {
            const idTrain = Number(item.idTrain);
            if (Number.isFinite(idTrain)) {
              values[idTrain] = parseNullableNumber(item.tciValue);
            }
            return values;
          }, {}),
        );
      })
      .catch(() => {
        if (isCurrentRequest) {
          setTciValueByTrainId({});
        }
      })
      .finally(() => {
        if (isCurrentRequest) {
          setIsTciSortLoading(false);
        }
      });

    return () => {
      isCurrentRequest = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [stationId, startTimestamp, endTimestamp, sortField]);

  const sortItemsByTrainId = useMemo(() => {
    const items: Record<number, IStationTrainSortItem> = {};

    Object.keys(trains).forEach(key => {
      const id = Number(key);
      const nestedTrain = trains[id];
      const train = getTrain(id);
      const alarmStatus =
        nestedTrain?.alarmStatus ??
        (train?.Points?.length ? getMaxAlarmsStatusPointsOrVisualControl(train.Points, isVisualControlOpen) : 0);

      items[id] = {
        id,
        hasAlarmData: Boolean(train?.ldd) || (alarmStatus > 0 && alarmStatus !== 255),
        name: nestedTrain?.name || train?.TrainName || "",
        userSortNum:
          typeof nestedTrain?.userSortNum === "number"
            ? nestedTrain.userSortNum
            : typeof train?.UserSortNum === "number"
              ? train.UserSortNum
              : nestedTrain?.orderNumber ?? id,
        alarmStatus,
        tciValue: tciValueByTrainId[id] ?? null,
        modeColor: getModeColor(train?.ModeChangedAt, train?.ModeColor, resolveAlarmColor),
        forecastStatus: forecastStatusByTrainId[id] ?? null,
      };
    });

    return items;
  }, [trains, getTrain, forecastStatusByTrainId, tciValueByTrainId, isVisualControlOpen, resolveAlarmColor]);

  const trainsKeys = useMemo(
    () => sortStationTrainIds(Object.keys(trains).map(Number), sortItemsByTrainId, sortField, sortDirection),
    [trains, sortItemsByTrainId, sortField, sortDirection],
  );

  const visibleTrainIds = useMemo(
    () => trainsKeys.slice(0, Math.min(displayCount, trainsKeys.length)),
    [trainsKeys, displayCount],
  );

  const hasMoreTrains = displayCount < trainsKeys.length;

  const visibleTrainIdsRef = useRef<number[]>([]);
  visibleTrainIdsRef.current = visibleTrainIds;

  const loadPendingVisibleTilesRef = useRef<() => Promise<void>>(() => Promise.resolve());

  const loadPendingVisibleTiles = useCallback(async () => {
    if (
      !stationId ||
      !startTimestamp ||
      !endTimestamp ||
      !tilesListContextKey ||
      isSortDataLoading ||
      inFlightTrainIdsRef.current.size > 0
    ) {
      return;
    }

    const idsToLoad = visibleTrainIdsRef.current
      .filter(id => !loadedTrainIdsRef.current.has(id) && !inFlightTrainIdsRef.current.has(id))
      .slice(0, batchSizeRef.current);

    if (!idsToLoad.length) {
      return;
    }

    const requestKey = `${tilesListContextKey}|${idsToLoad.join(",")}`;
    if (activeTilesRequestKeyRef.current === requestKey) {
      return;
    }

    const requestGeneration = tilesRequestGenerationRef.current;
    activeTilesRequestKeyRef.current = requestKey;
    idsToLoad.forEach(id => inFlightTrainIdsRef.current.add(id));
    setIsLoadingMoreTiles(true);

    let shouldChainNextBatch = false;

    try {
      const trainIds = idsToLoad.join(",");
      const requestParams = {
        idTopLevel: stationId,
        startTimestamp,
        endTimestamp,
        analyticsRows: selectedAnalyticsRows,
        trainIds,
      };

      const tilesResponse = await loadTrainTiles(undefined, requestParams);

      if (requestGeneration !== tilesRequestGenerationRef.current) {
        return;
      }

      if (!Array.isArray(tilesResponse) || !tilesResponse.length) {
        return;
      }

      const mapped = mapApiTrainTiles(tilesResponse, analyticsRowsView, analyticsRowsVisibility);
      setTileDataByTrainId(previous => ({
        ...previous,
        ...mapped,
      }));

      idsToLoad.forEach(id => loadedTrainIdsRef.current.add(id));
      shouldChainNextBatch = true;
    } catch {
    } finally {
      if (activeTilesRequestKeyRef.current === requestKey) {
        activeTilesRequestKeyRef.current = null;
      }

      idsToLoad.forEach(id => inFlightTrainIdsRef.current.delete(id));

      if (requestGeneration === tilesRequestGenerationRef.current) {
        setIsLoadingMoreTiles(false);

        if (shouldChainNextBatch) {
          const hasMorePending = visibleTrainIdsRef.current.some(
            id => !loadedTrainIdsRef.current.has(id) && !inFlightTrainIdsRef.current.has(id),
          );

          if (hasMorePending) {
            void loadPendingVisibleTilesRef.current();
          }
        }
      } else {
        void loadPendingVisibleTilesRef.current();
      }
    }
  }, [
    stationId,
    startTimestamp,
    endTimestamp,
    tilesListContextKey,
    selectedAnalyticsRows,
    analyticsRowsView,
    analyticsRowsVisibility,
    isSortDataLoading,
    loadTrainTiles,
  ]);

  loadPendingVisibleTilesRef.current = loadPendingVisibleTiles;

  const visibleTrainIdsKey = useMemo(() => visibleTrainIds.join(","), [visibleTrainIds]);

  useEffect(() => {
    if (!tilesListContextKey || !visibleTrainIds.length) {
      return;
    }

    void loadPendingVisibleTilesRef.current();
  }, [tilesListContextKey, visibleTrainIdsKey, isSortDataLoading]);

  useEffect(() => {
    const scrollElement = scrollRef.current;
    const sentinelElement = sentinelRef.current;

    if (!scrollElement || !sentinelElement || !hasMoreTrains) {
      return;
    }

    const observer = new IntersectionObserver(
      entries => {
        if (!entries[0]?.isIntersecting || isLoadingMoreTiles) {
          return;
        }

        setDisplayCount(current => Math.min(current + batchSizeRef.current, trainsKeys.length));
      },
      { root: scrollElement, rootMargin: "120px", threshold: 0 },
    );

    observer.observe(sentinelElement);

    return () => observer.disconnect();
  }, [trainsKeys.length, hasMoreTrains, isLoadingMoreTiles]);

  const handleClick = useCallback(
    (trainId: number) => (event: MouseEvent) => {
      if (schemeInNewWindow) {
        openPopupWithParams(event, trainId);
      } else {
        selectTrain(trainId);
      }
    },
    [schemeInNewWindow, selectTrain],
  );

  const handleSortFieldChange = (field: StationTrainSortField) => {
    setSortField(field);
  };

  const handleSortDirectionToggle = () => {
    setSortDirection(current => (current === SortDirection.asc ? SortDirection.desc : SortDirection.asc));
  };

  return (
    <Box sx={{ display: "flex", flex: 1, flexDirection: "column", minHeight: 0 }}>
      <Stack
        direction="row"
        spacing={2}
        alignItems="center"
        justifyContent="space-between"
        sx={{ mb: 1, flexShrink: 0, minWidth: 0 }}
      >
        <Box sx={{ minWidth: 0, flex: 1 }}>
          <StationTrainSortToolbar
            direction={sortDirection}
            field={sortField}
            onDirectionChange={handleSortDirectionToggle}
            onFieldChange={handleSortFieldChange}
          />
        </Box>
        {headerActions}
      </Stack>
      <Box
        ref={scrollRef}
        sx={{
          display: "flex",
          flex: 1,
          flexDirection: "column",
          minHeight: 0,
          overflow: "auto",
        }}
      >
        <Box sx={{ display: "flex", flexWrap: "wrap" }}>
          {visibleTrainIds.map(id => {
            const nestedTrain = trains[id];
            const train = getTrain(id);
            const name = nestedTrain.name || train?.TrainName || "";
            const alarmStatus =
              nestedTrain?.alarmStatus ??
              (train?.Points?.length ? getMaxAlarmsStatusPointsOrVisualControl(train.Points, isVisualControlOpen) : 0);
            const tileData = tileDataByTrainId[id];

            return (
              <StationTrain
                key={id}
                alarmStatus={alarmStatus}
                analyticsRowIds={selectedAnalyticsRowIds}
                analyticsRows={tileData?.analyticsRows ?? []}
                customTypeTitle={
                  train?.TrainCustomTypeId ? customTrainTypeNames[train.TrainCustomTypeId] ?? null : null
                }
                defectIcons={tileData?.defectIcons ?? []}
                description={train?.TrainDescription ?? null}
                equipmentGroupName={train?.idEquipmentGroup ? equipmentGroupNames[train.idEquipmentGroup] : undefined}
                handleClick={handleClick}
                hasTciParameters={tileData?.hasTciParameters === true}
                id={id}
                measureRows={tileData?.measureRows ?? []}
                modeChangedAt={train?.ModeChangedAt}
                modeColor={train?.ModeColor}
                name={name}
                tciValue={tileData?.tciValue ?? train?.TciValue}
                trainColour={train?.TrainColour ?? nestedTrain.trainColour}
                trainInstanceNumber={train?.TrainInstanceNumber}
                trainType={train?.TrainType}
              />
            );
          })}
        </Box>

        {hasMoreTrains ? <Box ref={sentinelRef} sx={{ width: "100%", height: 1, flexShrink: 0 }} /> : null}

        {isLoadingMoreTiles || isSortDataLoading ? (
          <Box sx={{ display: "flex", justifyContent: "center", py: 2 }}>
            <CircularProgress size={28} />
          </Box>
        ) : null}
      </Box>
    </Box>
  );
};
